import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// SUPPORTER SERVICE — Ni-kshay Mitra / nutrition / CSR / volunteer registration
// Public register POST /supporters/register; admin review GET/PUT/DELETE.
// ================================================================================

export type RegistrationType =
  | 'NIKSHAY_MITRA_INDIVIDUAL'
  | 'NIKSHAY_MITRA_NGO'
  | 'NIKSHAY_MITRA_CORPORATE'
  | 'NUTRITION_SPONSOR'
  | 'VOLUNTEER'

export type SupportType =
  | 'NUTRITION'
  | 'FAMILY_SUPPORT'
  | 'PSYCHOSOCIAL_SUPPORT'
  | 'OTHER_APPROVED'
  | 'EMOTIONAL_SUPPORT'
  | 'FOLLOW_UP'
  | 'DIAGNOSTICS'
  | 'AWARENESS'
  | 'TRANSPORT'
  | 'DIGITAL_TOOLS'
  | 'OTHER'

export type RegistrationStatus = 'NEW' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'ONBOARDED'

export interface SupporterRegistration {
  id: number
  registrationUuid?: string
  registrationType: RegistrationType
  name: string
  email: string
  phone?: string
  organizationName?: string
  organizationType?: string
  city?: string
  state?: string
  district?: string
  taluka?: string
  patientSupportOption?: string
  preferredSupportType?: SupportType
  supportTypeDetail?: string
  commitmentLevel?: string
  numberOfPatients?: number
  message?: string
  status: RegistrationStatus
  adminNotes?: string
  reviewedBy?: number
  reviewedAt?: string
  createdAt: string
}

export interface SupporterRegistrationRequest {
  registrationType: RegistrationType
  name: string
  email: string
  phone?: string
  organizationName?: string
  organizationType?: string
  city?: string
  state?: string
  district?: string
  taluka?: string
  patientSupportOption?: string
  preferredSupportType?: SupportType
  supportTypeDetail?: string
  commitmentLevel?: string
  numberOfPatients?: number
  message?: string
}

/** POST /supporters/register — public submission. */
export async function registerSupporter(req: SupporterRegistrationRequest): Promise<SupporterRegistration> {
  const res = await api.post<ApiResponse<SupporterRegistration>>('/supporters/register', req)
  return res.data.data
}

/** GET /supporters/me — the logged-in supporter's own registrations (real data only). */
export async function getMySupporterRegistrations(): Promise<SupporterRegistration[]> {
  const res = await api.get<ApiResponse<SupporterRegistration[]>>('/supporters/me')
  return res.data.data
}

// ---- public "Track my support" ----

export interface SupporterStatusEntry {
  status: RegistrationStatus
  note?: string
  at: string
}

export interface SupporterTrackResponse {
  registrationUuid: string
  name: string
  registrationType: RegistrationType
  preferredSupportType?: SupportType
  supportTypeDetail?: string
  city?: string
  state?: string
  district?: string
  taluka?: string
  patientSupportOption?: string
  status: RegistrationStatus
  adminNotes?: string
  createdAt: string
  reviewedAt?: string
  timeline: SupporterStatusEntry[]
}

/** GET /supporters/track/{uuid} — public lookup by the reference given at registration. */
export async function trackSupporter(uuid: string): Promise<SupporterTrackResponse> {
  const res = await api.get<ApiResponse<SupporterTrackResponse>>(`/supporters/track/${encodeURIComponent(uuid)}`)
  return res.data.data
}

// ---- admin review (ADMIN / SUPER_ADMIN) ----

export async function getSupporterRegistrations(params: {
  status?: string
  type?: string
  search?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<SupporterRegistration>> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.type) qs.set('type', params.type)
  if (params.search) qs.set('search', params.search)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<SupporterRegistration>>>(`/supporters?${qs.toString()}`)
  return res.data.data
}

export async function getSupporterStats(): Promise<Record<string, number>> {
  const res = await api.get<ApiResponse<Record<string, number>>>('/supporters/stats')
  return res.data.data
}

export async function updateSupporterStatus(
  id: number,
  status: RegistrationStatus,
  notes?: string,
): Promise<SupporterRegistration> {
  const qs = new URLSearchParams({ status })
  if (notes !== undefined) qs.set('notes', notes)
  const res = await api.put<ApiResponse<SupporterRegistration>>(`/supporters/${id}/status?${qs.toString()}`)
  return res.data.data
}

export async function deleteSupporterRegistration(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/supporters/${id}`)
}

export const REGISTRATION_TYPE_LABELS: Record<RegistrationType, string> = {
  NIKSHAY_MITRA_INDIVIDUAL: 'TB Supporter — individual (Ni-kshay Mitra)',
  NIKSHAY_MITRA_NGO: 'TB Supporter — NGO (Ni-kshay Mitra)',
  NIKSHAY_MITRA_CORPORATE: 'TB Supporter — institution (Ni-kshay Mitra)',
  NUTRITION_SPONSOR: 'Nutrition sponsor',
  VOLUNTEER: 'Volunteer',
}

export const SUPPORT_TYPE_LABELS: Record<SupportType, string> = {
  NUTRITION: 'Nutrition support',
  FAMILY_SUPPORT: 'Family support',
  PSYCHOSOCIAL_SUPPORT: 'Psychosocial support',
  OTHER_APPROVED: 'Other approved support',
  EMOTIONAL_SUPPORT: 'Emotional / social support',
  FOLLOW_UP: 'Treatment follow-up',
  DIAGNOSTICS: 'Diagnostics / screening',
  AWARENESS: 'Awareness & education',
  TRANSPORT: 'Patient transport',
  DIGITAL_TOOLS: 'Digital tools',
  OTHER: 'Other',
}

export const SUPPORTER_STATUS_LABELS: Record<RegistrationStatus, string> = {
  NEW: 'New',
  UNDER_REVIEW: 'Under review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ONBOARDED: 'Onboarded',
}

// ---- guided wizard constants ----

/** The four user-facing "how I want to help" options (wizard step 2). */
export const WIZARD_SUPPORT_TYPES: SupportType[] = [
  'NUTRITION',
  'FAMILY_SUPPORT',
  'PSYCHOSOCIAL_SUPPORT',
  'OTHER_APPROVED',
]

/** Guided delivery / patient-support options (wizard step 3). */
export const PATIENT_SUPPORT_OPTIONS = [
  'support_near_me',
  'through_tb_centre',
  'sponsor_nutrition',
  'community_support',
] as const
export type PatientSupportOption = (typeof PATIENT_SUPPORT_OPTIONS)[number]